import React, {Suspense, lazy, memo, useCallback, useMemo, useRef, useState} from 'react';
import {INotificationServiceEventResponse, INotificationServiceEventsResponse, NotificationModel} from '@techsmith/notifications-client';
import {Glyphs, TscStyles, TscThemeName} from '@techsmith/tsc-cloud-style-guide';
import userApi from '../../service/userApi';
import {useTranslation} from 'react-i18next';
import logging from '../../service/logging';
import config from '../../service/config';
import {importNotificationsClientPlugin} from '../../hooks/dynamicImport';
import styled from 'styled-components';
import Constants from '../../constants/Constants';
import AcceptTeamInvitationModal from '../modals/AcceptTeamInvitationModal';
import {useTeamsStore} from '../../context/teamsStore';
import {useShallow} from 'zustand/react/shallow';

const FallbackWrapper = styled.div`
   display: flex;
   align-items: center;
   justify-content: center;
   width: 32px;
   height: 32px;

   svg {
      padding: .5rem;
      width: 16px;
      height: 16px;
   }
`;

interface HideNotificationModel extends NotificationModel {
   hideNotification?: boolean;
}

const NotificationsWidget: React.FC<INotificationsWidgetProps> = ({currentUserId}) => {
   const {t} = useTranslation();
   const [showAcceptTeamInvitationModal, setShowAcceptTeamInvitationModal] = useState(false);
   const isFetchingNotifications = useRef(false);
   const isFetchingNotificationCount = useRef(false);
   const [teamInvitationData, setTeamInvitationData] = useState({
      teamId: '',
      teamName: '',
      invitationToken: ''
   });

   const {teamInvitations, userTeamsFolderTrees} = useTeamsStore(useShallow(s => ({
      teamInvitations: s.teamInvitations,
      userTeamsFolderTrees: s.userTeamsFolderTrees
   })));

   const invitationExists = useCallback(
      (invitationToken: string) => teamInvitations?.data.some(invite => invite.invitationId === invitationToken),
      [teamInvitations]
   );

   const isTeamMember = useCallback(
      (teamId: string) => userTeamsFolderTrees?.some(team => team.TeamId === teamId),
      [userTeamsFolderTrees]
   );

   const notificationTranslationHelper = useCallback(
      async (notificationResponse: INotificationServiceEventResponse, notificationType: string, hideDisplayName?: boolean, urlOverride?: string | null) => {
         const notificationModel: HideNotificationModel = Object.assign({}, notificationResponse, {
            header: t(`notification.${notificationType}.header`, notificationResponse.props),
            body: t(`notification.${notificationType}.body`, notificationResponse.props)
         });

         if (notificationType === 'teamInvitation') {
            if (!(invitationExists(notificationResponse.props.InvitationToken as string))) {
               if (!notificationModel.isRead) {
                  await userApi.markNotificationRead(notificationModel.externalId);
               }
               notificationModel.hideNotification = true;
               return Promise.resolve(notificationModel);
            }
         }

         if (notificationType === 'teamJoined') {
            if (!(isTeamMember(notificationResponse.originId))) {
               if (!notificationModel.isRead) {
                  await userApi.markNotificationRead(notificationModel.externalId);
               }
               notificationModel.hideNotification = true;
               return Promise.resolve(notificationModel);
            }
         }

         if (hideDisplayName) {
            notificationModel.hideDisplayName = true;
         }
         // note: null and undefined are different cases, explicitly passing in null to this func means 'disable' the url link from the
         // original message, not passing it in (leaving as undefined) means leave the original message url intact with no changes
         // eslint-disable-next-line no-undefined
         if (urlOverride !== undefined) {
            notificationModel.url = urlOverride;
         }
         return Promise.resolve(notificationModel);
      },
      [invitationExists, isTeamMember, t]
   );

   const notificationTranslationOverride = useCallback(
      async (notificationResponse: INotificationServiceEventResponse) => {
         switch (notificationResponse.eventType.toLowerCase()) {
            case 'captioningAssigned'.toLowerCase():
               return await notificationTranslationHelper(notificationResponse, 'captioningAssigned', true, notificationResponse.props.CallToActionUrl as string);
            case 'captionsPublished'.toLowerCase():
               return await notificationTranslationHelper(notificationResponse, 'captionsPublished', true, `${notificationResponse.props.MediaUrl as string}?view=editDetails`);
            case 'collectionInvite'.toLowerCase():
               return await notificationTranslationHelper(notificationResponse, 'collectionInvite', true, notificationResponse.props.CallToActionUrl as string);
            case 'collectionMediaAdded'.toLowerCase():
               return await notificationTranslationHelper(notificationResponse, 'collectionMediaAdded', false, notificationResponse.props.CollectionUrl as string);
            case 'encodingFailed'.toLowerCase():
               return await notificationTranslationHelper(notificationResponse, 'encodingFailed', true, notificationResponse.props.CallToActionUrl as string);
            case 'expirationWarning'.toLowerCase():
               return await notificationTranslationHelper(notificationResponse, 'expirationWarning', true, notificationResponse.props.CallToActionUrl as string);
            case 'featureSwitchChanged'.toLowerCase():
               return await notificationTranslationHelper(notificationResponse, 'featureSwitchChanged', true, null);
            case 'firstUpload'.toLowerCase():
               return await notificationTranslationHelper(notificationResponse, 'firstUpload', true, notificationResponse.props.MediaUrl as string);
            case 'firstView'.toLowerCase():
               return await notificationTranslationHelper(notificationResponse, 'firstView', true, notificationResponse.props.MediaUrl as string);
            case 'mediaReady'.toLowerCase():
               return await notificationTranslationHelper(notificationResponse, 'mediaReady', true, notificationResponse.props.MediaUrl as string);
            case 'newComment'.toLowerCase():
               return await notificationTranslationHelper(notificationResponse, 'newComment', false);
            case 'newCommentReply'.toLowerCase():
               return await notificationTranslationHelper(notificationResponse, 'newCommentReply', false);
            case 'quotaReached'.toLowerCase():
               return await notificationTranslationHelper(notificationResponse, 'quotaReached', true, notificationResponse.props.CallToActionUrl as string);
            case 'quotaWarning'.toLowerCase():
               return await notificationTranslationHelper(notificationResponse, 'quotaWarning', true, notificationResponse.props.CallToActionUrl as string);
            case 'teamInvitation'.toLowerCase():
               return await notificationTranslationHelper(notificationResponse, 'teamInvitation', false, `teams?notificationid=${notificationResponse.externalId}&teamid=${notificationResponse.props.TeamId as string}&teamname=${notificationResponse.props.TeamName as string}&invitationtoken=${notificationResponse.props.InvitationToken as string}`);
            case 'teamJoined'.toLowerCase():
               return await notificationTranslationHelper(notificationResponse, 'teamJoined', true, `${Constants.navigation.teamsLink}/${notificationResponse.props.TeamId as string}`);
            case 'transcriptionAutomaticComplete'.toLowerCase():
               return await notificationTranslationHelper(notificationResponse, 'transcriptionAutomaticComplete', true, notificationResponse.props.CallToActionUrl as string);
            case 'transcriptionAutomaticFailed'.toLowerCase():
               return await notificationTranslationHelper(notificationResponse, 'transcriptionAutomaticFailed', true, notificationResponse.props.MediaUrl as string);
            case 'transcriptionThirdPartyDenied'.toLowerCase():
               return await notificationTranslationHelper(notificationResponse, 'transcriptionThirdPartyDenied', true, notificationResponse.props.MediaUrl as string);
            case 'transcriptionThirdPartyFailed'.toLowerCase():
               return await notificationTranslationHelper(notificationResponse, 'transcriptionThirdPartyFailed', true, notificationResponse.props.MediaUrl as string);
            case 'transcriptionThirdPartyRequested'.toLowerCase():
               return await notificationTranslationHelper(notificationResponse, 'transcriptionThirdPartyRequested', true, notificationResponse.props.CallToActionUrl as string);
            case 'uploadFailed'.toLowerCase():
               return await notificationTranslationHelper(notificationResponse, 'uploadFailed', true);
            case 'userAdd'.toLowerCase():
               return await notificationTranslationHelper(notificationResponse, 'userAdd', true);
            case 'userDelete'.toLowerCase():
               return await notificationTranslationHelper(notificationResponse, 'userDelete', true);
            default:
               logging.warn(`An unmapped notification of has been detected, marking as read, notification info [${JSON.stringify(notificationResponse)}]`);
               return Object.assign({}, notificationResponse, {
                  hideNotification: true,
                  header: notificationResponse.eventType,
                  body: `You received a message of unexpected type ${notificationResponse.eventType} with props ${JSON.stringify(notificationResponse.props)}`
               });
         }
      },
      [notificationTranslationHelper]
   );

   const apiGetNotificationsOverride = useCallback(async (_: string, __: string, cursor?: string, limit?: number): Promise<INotificationServiceEventsResponse> => {
      if (!isFetchingNotifications.current) {
         try {
            isFetchingNotifications.current = true;
            return await userApi.getNotifications(cursor, limit);
         } finally {
            isFetchingNotifications.current = false;
         }
      }
      return {userNotifications: [], cursor: ''};
   }, [isFetchingNotifications]);

   const apiGetUnreadNotificationsCountOverride = useCallback(async () => {
      if (!isFetchingNotificationCount.current) {
         try {
            isFetchingNotificationCount.current = true;
            return await userApi.getNotificationsCount();
         } finally {
            isFetchingNotificationCount.current = false;
         }
      }
      return 0;
   }, [isFetchingNotificationCount]);

   const apiMarkAllAsReadOverride = useCallback(async (): Promise<void> => await userApi.markAllNotificationsRead(), []);

   const apiMarkAsReadOverride = useCallback(async (notificationId: string): Promise<void> => await userApi.markNotificationRead(notificationId), []);

   const NotificationsClientPlugin = useMemo(() => lazy(importNotificationsClientPlugin), []);

   const handleOpenModal = async (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      const targetUrl = (e.target as Element).getAttribute('href');

      if (targetUrl && targetUrl.split('?')[0] === 'teams') {
         window.stop(); // Prevent window redirecting
         const urlParams = new URLSearchParams(targetUrl.split('?')[1]);

         setTeamInvitationData({
            ...teamInvitationData,
            teamName: urlParams.get('teamname'),
            teamId: urlParams.get('teamid'),
            invitationToken: urlParams.get('invitationtoken')
         });
         setShowAcceptTeamInvitationModal(true);
         await userApi.markNotificationRead(urlParams.get('notificationid')); // This gets called again because the window.stop() aborts the first call
      }
   };

   const serverProxyConfig = useMemo(() => ({
      apiGetNotificationsOverride,
      apiGetUnreadNotificationsCountOverride,
      apiMarkAllAsReadOverride,
      apiMarkAsReadOverride
   }), [apiGetNotificationsOverride, apiGetUnreadNotificationsCountOverride, apiMarkAllAsReadOverride, apiMarkAsReadOverride]);

   return (
      <div className="notifications-button">
         <Suspense fallback={(
            <FallbackWrapper>
               <Glyphs.Alert16x16 />
            </FallbackWrapper>
         )}
         >
            <div onClick={handleOpenModal}>
               <NotificationsClientPlugin
                  themeName={TscThemeName.dusk} // theme is hardcoded to dusk because header is dusk
                  serviceName={config.featureSwitches.IsScreencast ? 'Screencast' : 'Knowmia'}
                  currentUserId={currentUserId}
                  palette={{
                     unreadBackgroundGradientStartRgb: TscStyles.color.action.screencastPrimaryStop1,
                     unreadBackgroundGradientStopRgb: TscStyles.color.action.screencastPrimaryStop2,
                     unreadHighlightRgb: TscStyles.color.brand.screencast,
                     unreadCountBackgroundRgba: `${TscStyles.color.brand.screencast}33`
                  }}
                  serverProxyConfig={serverProxyConfig}
                  notificationTranslation={n => notificationTranslationOverride(n)}
               />
            </div>
         </Suspense>

         {showAcceptTeamInvitationModal && (
            <AcceptTeamInvitationModal
               onClose={() => {
                  setShowAcceptTeamInvitationModal(false);
               }}
               teamId={teamInvitationData.teamId}
               teamName={teamInvitationData.teamName}
               invitationId={teamInvitationData.invitationToken}
            />
         )}
      </div>
   );
};

export interface INotificationsWidgetProps {
   currentUserId: string;
}

export default memo(NotificationsWidget);
